import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { CREATE_TINYCMS_VERSION_PREVIEW } from '../../graphql';
import { Loader } from '../common/PageLoader';
import { PostPreview } from '../reach/PostPreview';
import { postWords } from '../reach/postWords';
import type { Channel } from '../reach/reach';
import { Device, DeviceSwitch, frameStyleFor, useFrameScale } from './deviceFrame';
import { HistoryData, HistoryVersion, seeNote } from './historyWords';

/**
 * History's See this version: what one version looked like. A page or article
 * is the real site in a frame, on a link that shows only that version (the
 * apiserver's createVersionPreview), frozen so its links and forms do nothing
 * (smagent-sites PreviewOutline ?frozen=1). A post is the card Write a post
 * draws. Nothing here saves anything.
 */

const FRAME_HEIGHT = 'calc(100vh - 250px)';

/** Links made this session, per version, so stepping back and forth makes none. */
const minted = new Map<string, { src: string; expiresAt: number }>();

/** A minted link as this frame opens it. */
export function frozenAddress(url: string) {
  const u = new URL(url);
  u.searchParams.set('embed', '1');
  u.searchParams.set('frozen', '1');
  return u.toString();
}

function VersionFrame({
  kind,
  id,
  version,
  device,
}: {
  kind: 'article' | 'page';
  id: string;
  version: number;
  device: Device;
}) {
  const { accountId } = useAccount();
  const [create] = useMutation(CREATE_TINYCMS_VERSION_PREVIEW);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { wrapRef, scale } = useFrameScale(device);

  useEffect(() => {
    if (!accountId) return;
    const key = `${accountId}:${kind}:${id}:${version}`;
    const known = minted.get(key);
    setError('');
    if (known && known.expiresAt > Date.now()) {
      setSrc(known.src);
      return;
    }
    let alive = true;
    setSrc(null);
    create({ variables: { accountId, kind, id, version } })
      .then(({ data }) => {
        const made = data?.createTinycmsVersionPreview;
        if (!made?.url) throw new Error('This version couldn’t load.');
        const address = frozenAddress(made.url);
        minted.set(key, { src: address, expiresAt: new Date(made.expiresAt).getTime() });
        if (alive) setSrc(address);
      })
      .catch((err: Error) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [accountId, kind, id, version, create]);

  return (
    <div className="pg-stage">
      <div
        ref={wrapRef}
        className="pg-frame-wrap"
        data-device={device}
        style={scale < 1 || error || !src ? { height: FRAME_HEIGHT } : undefined}
      >
        {error ? (
          <p className="pg-frame-note" role="alert">
            {error}
          </p>
        ) : src ? (
          <iframe
            key={src}
            title={`Version ${version} as it looked`}
            src={src}
            className="pg-frame"
            sandbox="allow-scripts allow-same-origin"
            style={frameStyleFor(scale, FRAME_HEIGHT)}
          />
        ) : (
          <Loader label="Loading this version" className="pg-frame-note" />
        )}
      </div>
    </div>
  );
}

/** The channel a post version is drawn on, once it's disconnected only its network. */
export function postChannel(history: HistoryData): Channel {
  const p = history.channelProfile;
  return {
    id: p?.id || 'post',
    platform: p?.platform || history.platform || '',
    profileId: p?.profileId,
    profileName: p?.profileName,
    profileUsername: p?.profileUsername,
    isActive: true,
    approvalMode: 'ask',
  };
}

export function VersionLook({
  kind,
  id,
  version,
  history,
  actions,
}: {
  kind: 'article' | 'page' | 'post';
  id: string;
  version: HistoryVersion;
  history: HistoryData;
  /** History's buttons, beside the size switch. */
  actions: React.ReactNode;
}) {
  const [device, setDevice] = useState<Device>('desktop');
  const look = version.look;
  return (
    <>
      <div className="hv-actions">
        {actions}
        {kind === 'post' ? null : <DeviceSwitch device={device} onDevice={setDevice} />}
      </div>
      <p className="hv-note">{seeNote(kind, version)}</p>
      {kind === 'post' ? (
        <PostPreview
          channels={[postChannel(history)]}
          textFor={() => postWords(look || {})}
          link={look?.postUrl || ''}
          imageUrl={look?.imageUrl || null}
        />
      ) : (
        <VersionFrame kind={kind} id={id} version={version.v} device={device} />
      )}
    </>
  );
}
